import { useState, useEffect, useCallback } from "react";
import { IS_NATIVE_APP } from "../config/appTarget.js";
import {
  getOnboardingCompleted,
  setOnboardingCompleted,
} from "../lib/appStorage.js";

interface UseOnboardingResult {
  completed: boolean;
  loading: boolean;
  markCompleted: () => Promise<void>;
}

// Module-level shared state so every mounted useOnboarding instance
// stays in sync — fixes the guard/carousel split-state loop.
type Listener = (completed: boolean) => void;
const _listeners = new Set<Listener>();
let _cached: boolean | null = null;
let _fetchPromise: Promise<void> | null = null;

/**
 * On web: onboarding is always considered complete — no carousel is shown.
 * On iOS: reads/writes `onboarding_completed` from `@capacitor/preferences`.
 * All hook instances share a single in-memory cache so markCompleted() in
 * the carousel immediately unblocks the OnboardingGuard without a storage
 * round-trip.
 */
export function useOnboarding(): UseOnboardingResult {
  const [completed, setCompleted] = useState(
    !IS_NATIVE_APP || _cached === true,
  );
  const [loading, setLoading] = useState(IS_NATIVE_APP && _cached === null);

  useEffect(() => {
    const listener: Listener = (val) => {
      setCompleted(val);
      setLoading(false);
    };
    _listeners.add(listener);

    if (IS_NATIVE_APP && _cached === null && !_fetchPromise) {
      _fetchPromise = getOnboardingCompleted().then((done) => {
        _cached = done;
        _listeners.forEach((l) => l(done));
      });
    }

    return () => {
      _listeners.delete(listener);
    };
  }, []);

  const markCompleted = useCallback(async () => {
    _cached = true;
    if (IS_NATIVE_APP) {
      await setOnboardingCompleted(true);
    }
    _listeners.forEach((l) => l(true));
  }, []);

  return { completed, loading, markCompleted };
}
